import { storageKeys } from '@/app/constants/storageKeys';
import { IStorage } from '@/infra/interfaces/storage/storage';

interface CookieAttributes {
  path?: string;
  domain?: string;
  secure?: boolean;
  expires?: string | Date;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

interface Cookies {
  remove(name: string): void;
  get(name: string): string | void;
  add(name: string, value: string, attributes?: CookieAttributes): void;
}

const dayInMilliseconds = 86400000;

const cookies: Cookies = {
  add(name: string, value: string, attributes?: CookieAttributes) {
    if (typeof document === 'undefined') {
      return;
    }

    name = encodeURIComponent(name);

    let stringify = `${name}=${encodeURIComponent(value)}`;

    attributes = {
      path: '/',
      sameSite: 'Lax' as const,
      secure: typeof location !== 'undefined' ? location.protocol === 'https:' : false,
      ...attributes,
    };

    if (attributes.expires && typeof attributes.expires !== 'string') {
      attributes.expires = attributes.expires.toUTCString();
    }

    Object.entries(attributes).forEach(([key, value]) => {
      stringify += `; ${key}=${value}`;
    });

    document.cookie = stringify;
  },
  get(name: string) {
    if (typeof document === 'undefined') {
      return;
    }

    name = encodeURIComponent(name);

    const cookies = document.cookie ? document.cookie.split('; ') : [];

    const items = cookies.map((cookie) => {
      const splited = cookie.split('=');
      return {
        key: splited[0],
        value: splited[1],
      };
    });

    const foundKey = items.find(({ key }) => key === name)?.value ?? '';

    return decodeURIComponent(foundKey);
  },
  remove(name: string) {
    this.add(name, '', {
      expires: new Date(Date.now() - dayInMilliseconds).toUTCString(),
    });
  },
};

/**
 * Secure cookie storage adapter that uses custom cookie implementation
 * Note: This is a client-side implementation. For true HttpOnly cookies,
 * the server must set them via Set-Cookie header
 */
export class SecureCookieAdapter implements IStorage {
  private readonly cookieOptions: CookieAttributes;

  constructor() {
    this.cookieOptions = {
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'Lax',
      expires: new Date(Date.now() + dayInMilliseconds),
      path: '/',
    };
  }

  /**
   * Get value from secure cookie
   */
  get(key: string): string {
    return cookies.get(key) || '';
  }

  /**
   * Add or override value in secure cookie
   */
  add(key: string, value: string): void {
    let expires: Date | undefined = undefined;

    if (key === storageKeys.ACCESS_TOKEN) {
      expires = new Date(Date.now() + 15 * 60 * 1000);
    }

    if (key === storageKeys.REFRESH_TOKEN) {
      expires = new Date(Date.now() + 7 * dayInMilliseconds);
    }

    const attributes: CookieAttributes = {
      ...this.cookieOptions,
      ...(expires ? { expires } : {}),
    };

    if (key === storageKeys.ACCESS_TOKEN || key === storageKeys.REFRESH_TOKEN) {
      attributes.sameSite = 'Strict';
    }

    cookies.add(key, value, attributes);
  }

  /**
   * Remove value from secure cookie
   */
  remove(key: string): void {
    cookies.remove(key);
  }

  /**
   * Clear all cookies (only those accessible to client)
   */
  clear(): void {
    // Get all cookies and remove them
    const allCookies = document.cookie.split(';');
    allCookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.slice(0, eqPos).trim() : cookie.trim();
      cookies.remove(name);
    });
  }

  /**
   * Clear cookies except for specified values
   */
  clearExcept(...except: string[]): void {
    const allCookies = document.cookie.split(';');
    const cookiesToKeep = new Set(except);

    allCookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.slice(0, eqPos).trim() : cookie.trim();

      if (!cookiesToKeep.has(name)) {
        cookies.remove(name);
      }
    });
  }

  /**
   * Get and remove value from secure cookie
   */
  take(key: string): string {
    const value = this.get(key);
    this.remove(key);
    return value;
  }
}
